import React, { useState } from "react";
import Page from "components/layouts/Page";
import {
  ApiException,
  BatchDefaultsHousingAuthorityScalingForm,
  BatchDefaultsHousingAuthorityScalingFormClient,
  BaselineDefaultsHousingAuthorityScalingForm
} from "api";
import RequirePermission from "../RequirePermission";
import {
  useRemoteData,
  responseHandlers,
  makeRemoteRequest
} from "components/useRemoteData";
import { useApiEndpoint, getApiEndpoint } from "components/useApiEndpoint";
import BatchDefaultsMenu from "../BatchDefaultsMenu";
import { RouteComponentProps } from "react-router";
import { useContext } from "react";
import { IdentityContext } from "components/IdentityProvider";
import BatchDefaultsBatchInformationArea from "components/forms/BatchDefaultsBatchInformationArea";
import Spinner from "components/controls/Spinner";
import useMessageArea from "components/useMessageArea";
import DefaultsHousingAuthorityScalingForm from "../DefaultsHousingAuthorityScalingForm";
import { BatchDefaultsEditMode } from "components/routes";
import utils from "utils";

interface MatchParams {
  id: string;
  mode: BatchDefaultsEditMode;
}

interface PageProps extends RouteComponentProps<MatchParams> {}

interface PageState {
  form: BatchDefaultsHousingAuthorityScalingForm | undefined;
  hasFormChanged: boolean;
}

interface LoadingState {
  isLoading: boolean;
}

const BatchDefaultsHousingAuthorityScalingPage: React.FC<PageProps> = function (
  props: PageProps
) {
  const batchKey: string = props.match.params.id;
  const batchDefaultsEditMode: BatchDefaultsEditMode = props.match.params.mode;

  const [page, setPage] = useState<PageState>({
    form: undefined,
    hasFormChanged: false
  });

  const [
    messageAreaState,
    setMessageBasedOnForm,
    setMessageBasedOnApiException
  ] = useMessageArea();

  const setDefaultsForm = function (
    defaultsForm: BaselineDefaultsHousingAuthorityScalingForm | null,
    hasFormChanged: boolean = true
  ) {
    if (!defaultsForm) {
      return;
    }

    setPage({
      ...page,
      form: {
        ...page.form,
        defaults: {
          ...defaultsForm
        }
      },
      hasFormChanged: hasFormChanged
    });
  };

  const onUpdate = function (
    form: BatchDefaultsHousingAuthorityScalingForm | undefined | null,
    hasFormChanged: boolean = true
  ): void {
    setPage({
      ...page,
      form: {
        ...form,
        defaults: {
          ...(form && form.defaults)
        }
      },
      hasFormChanged: hasFormChanged
    });
  };

  const [dataState] = useRemoteData(
    useApiEndpoint(
      BatchDefaultsHousingAuthorityScalingFormClient,
      "getBatchDefaultsHousingAuthorityScalingForm"
    ),
    [batchKey],
    {
      autoFetch: true,
      onChange: (form) => {
        onUpdate(form, false);
      }
    }
  );

  const handleSubmitError = function (error: ApiException) {
    responseHandlers.use({
      "400": (ex: ApiException) => {
        const form = ex.result;
        setMessageBasedOnForm(form);
        setPage({ ...page, form: form });
      },
      _: (ex) => {
        setMessageBasedOnApiException(ex);
      }
    })(error);
  };
  const submitEndpoint = "saveBatchDefaultsHousingAuthorityScalingForm";
  const [submitState, submit] = useRemoteData(
    useApiEndpoint(BatchDefaultsHousingAuthorityScalingFormClient, submitEndpoint),
    [batchKey, page.form || {}],
    {
      onChange: (form) => {
        setMessageBasedOnForm(form);
        onUpdate(form);
      },
      onError: handleSubmitError
    }
  );

  const [loadingState, setLoadingState] = useState<LoadingState>({
    isLoading: false
  });
  const identity = useContext(IdentityContext);
  const saveFormPromise = (): Promise<any> => {
    if (!page.hasFormChanged) {
      return utils.getEmptyPromise();
    }

    setLoadingState({ isLoading: true });

    return makeRemoteRequest(
      getApiEndpoint(
        identity,
        BatchDefaultsHousingAuthorityScalingFormClient,
        submitEndpoint
      ),
      [batchKey, page.form || {}]
    )
      .catch((error) => {
        handleSubmitError(error);
        return utils.getRejectedPromise();
      })
      .finally(() => setLoadingState({ isLoading: false }));
  };

  const batch = page.form && page.form.batchDetail;
  const spinner = (
    <Spinner
      isVisible={
        dataState.isFetching || submitState.isFetching || loadingState.isLoading
      }
    />
  );

  //See T-4258 for refactoring improvements
  if (page.form == null && (dataState.isFetching || submitState.isFetching)) {
    return spinner;
  } else if (page.form == null) {
    return messageAreaState.messageArea;
  }

  if (!page.form.defaults) {
    return <span>Error with current defaults.</span>;
  }

  if (!batch) {
    return <span>Error with batch information.</span>;
  }

  const menu = (
    <BatchDefaultsMenu
      onClick={saveFormPromise}
      batchKey={batchKey}
      title={"HOUSING AUTHORITY SCALING"}
      batchDefaultsEditMode={batchDefaultsEditMode}
      hasUnsavedChanges={page.hasFormChanged}
      pageConfiguration={page.form.defaults.pageConfiguration}
    />
  );

  return (
    <Page menu={menu}>
      <RequirePermission
        permissions={["canViewBatchDefaultsForms"]}
        accessDenied="page"
      >
        {spinner}
        {messageAreaState.messageArea}
        <BatchDefaultsBatchInformationArea batchInformation={{ ...batch }} />
        <DefaultsHousingAuthorityScalingForm
          isFetching={submitState.isFetching}
          submit={() => submit([batchKey, page.form || {}])}
          setForm={setDefaultsForm}
          form={page.form.defaults}
          hideDivisions={true}
          hideSaveButton={true}
          pageConfiguration={page.form.defaults.pageConfiguration}
        />
      </RequirePermission>
    </Page>
  );
};

export default BatchDefaultsHousingAuthorityScalingPage;
